/**
 * Radio - Quick message system
 *
 * Provides preset message channels (All, Team, Close Range) with
 * customizable messages. Persists to localStorage for returning players.
 * Pipe-separated messages are sent sequentially with delays.
 */

import { loadString, removeKey, saveJson } from '../services/LocalStorage';
import { RADIO_CHANNELS, uiState, type RadioChannel } from '../ui/UIState';
import { chunkForWire, sendChunked } from './Chat';
import { sendChat as networkSendChat, sendSay as networkSendSay, sendTeam as networkSendTeam } from './Network';
import { settings } from './Settings';

/** Three channels of messages, indexed by RadioChannel */
type ChannelMessages = [string[], string[], string[]];

/** Network send function per channel */
const CHANNEL_SEND: Record<RadioChannel, (text: string) => void> = {
  0: networkSendChat,
  1: networkSendTeam,
  2: networkSendSay,
};

/** Channel by the KeyboardEvent code of its fixed key */
const CHANNEL_BY_KEY_CODE = new Map<string, RadioChannel>(
  ([0, 1, 2] as const).map((channel) => [RADIO_CHANNELS[channel].code, channel]),
);

/** Slots per channel: the digit keys 1 to 0 */
const MESSAGES_PER_CHANNEL = 10;

/** Delay between pipe-split message parts (ms) */
const PIPE_SPLIT_DELAY = 1500;

/** localStorage key, StarMash's */
const STORAGE_KEY = 'radioMessages';

/** Default messages per channel */
const DEFAULT_MESSAGES: readonly [readonly string[], readonly string[], readonly string[]] = [
  [
    'gg',
    'Hi!',
    'Yes',
    'No',
    'Thanks!',
    'You are welcome!',
    "I'll be right back.",
    'Got to go, cya!',
    '-PING-',
    'Good luck, have fun!',
  ],
  [
    'Affirmative',
    'Negative',
    "** I'VE GOT THE FLAG! COVER ME! **",
    '** THE ENEMY HAS OUR FLAG! **',
    '** All wings, protect our flag carrier! **',
    'Defending the base! Need assistance!',
    'Attacking their base! Need assistance!',
    'On my way!',
    'Cancel that..',
    'Prowler in our base!',
  ],
  [
    'Hey there!',
    'Stop!',
    'Go Go Go!',
    'Imperial scum!',
    'Rebel scum!',
    'Prowler here!',
    'YEE-HAW!!!',
    ':lol:',
    ':cry:',
    ':clap:',
  ],
];

/** The messages, loaded on first use so a StarMash copy that arrives with the login is seen */
let messages: ChannelMessages | null = null;

function cloneDefaults(): ChannelMessages {
  return [[...DEFAULT_MESSAGES[0]], [...DEFAULT_MESSAGES[1]], [...DEFAULT_MESSAGES[2]]];
}

function isStringList(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((text) => typeof text === 'string');
}

/** A channel's messages cut or padded to the ten slots */
function toSlots(list: string[]): string[] {
  const slots = list.slice(0, MESSAGES_PER_CHANNEL);
  while (slots.length < MESSAGES_PER_CHANNEL) {
    slots.push('');
  }
  return slots;
}

/**
 * Three channels of strings from a save, or null. Ours is an array; StarMash's
 * is an object keyed 0, 1 and 2, because its default clone went through
 * jQuery.extend into an object, and that is the shape of its account copy too.
 */
function toChannelMessages(value: unknown): ChannelMessages | null {
  if (typeof value !== 'object' || value === null || !('0' in value && '1' in value && '2' in value)) {
    return null;
  }
  const all = value['0'];
  const team = value['1'];
  const say = value['2'];
  if (!isStringList(all) || !isStringList(team) || !isStringList(say)) {
    return null;
  }
  return [toSlots(all), toSlots(team), toSlots(say)];
}

/**
 * A StarMash player's messages, if the account carries them. The live StarMash
 * syncs its radio into the account settings, which arrive with the login.
 */
function accountMessages(): ChannelMessages | null {
  return toChannelMessages(settings.SWAM_RadioMessages);
}

/** The local save, else the account's StarMash copy, else the defaults */
function loadMessages(): ChannelMessages {
  // Blocked storage reads as empty; the fallbacks apply then
  const stored = loadString(STORAGE_KEY);
  if (stored) {
    try {
      const parsed: unknown = JSON.parse(stored);
      const saved = toChannelMessages(parsed);
      if (saved !== null) {
        return saved;
      }
    } catch {
      removeKey(STORAGE_KEY);
    }
  }
  return accountMessages() ?? cloneDefaults();
}

function getMessages(): ChannelMessages {
  if (messages === null) {
    messages = loadMessages();
  }
  return messages;
}

/** Save current messages to localStorage; with it blocked or full they still apply for this session */
function saveMessages(): void {
  saveJson(STORAGE_KEY, getMessages());
}

/** Sends a macro's pipe-separated parts in turn, each in the chunks the wire takes, as the chat box sends: a
 *  long or non-Latin part overran the one-byte wire length and reached the room garbled. A reconnect drops the
 *  rest rather than sending it into the next game. */
function sendWithPipeSplit(channel: RadioChannel, text: string): void {
  const chunks = text.split(/\s*\|\s*/).flatMap((part) => chunkForWire(part, channel === 2));
  sendChunked(chunks, CHANNEL_SEND[channel], PIPE_SPLIT_DELAY);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** The channel a fixed radio key opens, or null for any other key */
export function radioChannelForKeyCode(code: string): RadioChannel | null {
  return CHANNEL_BY_KEY_CODE.get(code) ?? null;
}

/** Whether the radio panel is currently visible */
export function radioIsVisible(): boolean {
  return uiState.radio !== null;
}

/** Toggle radio panel for a channel (show or hide if same channel) */
export function radioToggle(channel: RadioChannel): void {
  if (uiState.radio !== null && uiState.radio.activeChannel === channel) {
    radioHide();
  } else {
    radioShow(channel);
  }
}

/** Show the radio panel for a specific channel */
export function radioShow(channel: RadioChannel): void {
  // Mobile has no radio panel, so a keyboard there (a tablet's) would open
  // one unseen, and its digits would send.
  if (uiState.isMobile) return;
  uiState.radio = {
    activeChannel: channel,
    messages: [...getMessages()[channel]],
  };
}

/** Hide the radio panel */
export function radioHide(): void {
  uiState.radio = null;
}

/** Send a radio message by index (0-9) from the active channel */
export function radioSendMessage(index: number): void {
  const radio = uiState.radio;
  if (!radio) return;

  const channel = radio.activeChannel;
  const text = getMessages()[channel][index];
  if (text && text.length > 0) {
    sendWithPipeSplit(channel, text);
  }
  radioHide();
}

/** Get messages for a channel (for config panel) */
export function radioGetMessages(channel: RadioChannel): string[] {
  return [...getMessages()[channel]];
}

/** Save edited messages for a channel */
export function radioSaveConfig(channel: RadioChannel, edited: string[]): void {
  getMessages()[channel] = toSlots(edited);
  saveMessages();
}

/** Restore a single channel to default messages */
export function radioRestoreChannel(channel: RadioChannel): void {
  getMessages()[channel] = [...DEFAULT_MESSAGES[channel]];
  saveMessages();
}

/** Restore all channels to default messages */
export function radioRestoreAll(): void {
  messages = cloneDefaults();
  // StarMash removed the key here, so later changes to the defaults still
  // reached a restored player, and so does this. With an account copy the
  // next load would adopt that instead, so then the defaults are stored.
  if (accountMessages() === null) {
    removeKey(STORAGE_KEY);
  } else {
    saveMessages();
  }
}
